'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLangCurrency } from '@/contexts/LangCurrencyContext';

const NAV_LINKS = [
  {
    label: 'Features',
    href: '/features',
    dropdown: [
      { label: 'Inventory Intelligence', href: '/features#inventory' },
      { label: 'Precision Pricing', href: '/features#pricing' },
      { label: 'Relationship CRM', href: '/features#crm' },
      { label: 'Appraisal Tools', href: '/features#appraisal' },
    ],
  },
  {
    label: 'Solutions',
    href: '/solutions',
    dropdown: [
      { label: 'For Retailers', href: '/solutions#retailers' },
      { label: 'For Wholesalers', href: '/solutions#wholesalers' },
      { label: 'For Appraisers', href: '/solutions#appraisers' },
      { label: 'Multi-Location', href: '/solutions#multi-location' },
    ],
  },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Contact', href: '/contact' },
];

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const pathname = usePathname();
  const { lang, toggleLang } = useLangCurrency();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      suppressHydrationWarning
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0a0e1a]/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          <span className="text-base font-light text-white tracking-[0.42em]">
            G&nbsp;L&nbsp;A&nbsp;N&nbsp;Z
          </span>{' '}
          <span className="text-[10px] text-[#9ca3af] ml-1 tracking-wide hidden sm:inline">
            CRM
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS?.map((link) => (
            <div
              key={link?.href}
              className="relative"
              onMouseEnter={() => link?.dropdown && setOpenDropdown(link?.label)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                href={link?.href}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pathname === link?.href || pathname?.startsWith(link?.href + '/')
                    ? 'text-white bg-white/5'
                    : 'text-[#9ca3af] hover:text-white hover:bg-white/5'
                }`}
              >
                {link?.label}
                {link?.dropdown && (
                  <svg
                    className="w-3.5 h-3.5 opacity-60"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                )}
              </Link>

              {link?.dropdown && openDropdown === link?.label && (
                <div className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-white/10 bg-[#111827]/95 backdrop-blur-md shadow-xl shadow-black/40 py-1.5 z-50">
                  {link?.dropdown?.map((item) => (
                    <Link
                      key={item?.href}
                      href={item?.href}
                      className="block px-4 py-2 text-sm text-[#9ca3af] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {item?.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-white/10 text-sm font-semibold text-[#9ca3af] hover:text-white hover:border-white/25 transition-colors"
            aria-label="Switch language / เปลี่ยนภาษา"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c2.21 0 4-4.03 4-9s-1.79-9-4-9-4 4.03-4 9 1.79 9 4 9z"
              />
            </svg>
            {lang === 'EN' ? 'TH' : 'EN'}
          </button>
          <Link
            href="/auth"
            className="px-4 py-2 rounded-lg text-sm font-medium text-[#9ca3af] hover:text-white transition-colors"
          >
            Log in
          </Link>
          <Link
            href="/demo"
            className="px-4 py-2 rounded-lg border border-white/10 text-[#9ca3af] hover:text-white text-sm font-medium transition-colors"
          >
            Free Demo
          </Link>
          <Link
            href="/signup"
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] text-sm font-bold shadow-lg shadow-amber-500/20 transition-all hover:-translate-y-0.5"
          >
            Get Started
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden p-2 rounded-lg text-[#9ca3af] hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#0a0e1a]/98 backdrop-blur-md">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS?.map((link) => (
              <div key={link?.href}>
                <Link
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 rounded-lg text-sm font-medium text-[#d1d5db] hover:text-white hover:bg-white/5 transition-colors"
                >
                  {link?.label}
                </Link>
                {link?.dropdown && (
                  <div className="ml-4 mt-1 space-y-1">
                    {link?.dropdown?.map((item) => (
                      <Link
                        key={item?.href}
                        href={item?.href}
                        onClick={() => setMobileOpen(false)}
                        className="block px-3 py-2 rounded-lg text-xs text-[#9ca3af] hover:text-white hover:bg-white/5 transition-colors"
                      >
                        {item?.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 border-t border-white/5 flex flex-col gap-2">
              <button
                onClick={() => {
                  toggleLang();
                  setMobileOpen(false);
                }}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-white/10 text-sm font-semibold text-[#d1d5db] hover:text-white hover:bg-white/5 transition-colors"
                aria-label="Switch language / เปลี่ยนภาษา"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c2.21 0 4-4.03 4-9s-1.79-9-4-9-4 4.03-4 9 1.79 9 4 9z"
                  />
                </svg>
                {lang === 'EN' ? 'ไทย' : 'English'}
              </button>
              <Link
                href="/auth"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 rounded-lg text-sm font-medium text-[#9ca3af] hover:text-white hover:bg-white/5 transition-colors"
              >
                Log in
              </Link>
              <Link
                href="/auth?mode=signup"
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-[#0a0e1a] text-sm font-bold text-center"
              >
                Book a Demo
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
